// Accès Supabase Storage pour le bucket PRIVÉ `logos-documents`.
//
// Convention de chemin imposée par les policies Storage :
//   `<uuid-perimetre>/<nom-fichier>`
// où `<uuid-perimetre>` correspond à `current_proprietaire_id()` (espace
// Propriétaire) ou `current_agence_id()` (espace Agence). Le premier dossier de
// l'objet DOIT correspondre au périmètre authentifié, sinon l'écriture est
// refusée par RLS. La lecture passe exclusivement par une URL signée : aucune
// donnée d'identité n'est stockée en local (localStorage interdit).

import { supabase } from "./supabase";
import { dataUrlToBlob } from "./storage";

export const LOGOS_BUCKET = "logos-documents";

const MAX_LOGO_SIZE = 1024 * 1024; // 1 Mo
const ACCEPTED_MIME = ["image/png", "image/jpeg"];

/** Valide un fichier de logo. Retourne un message d'erreur, ou null si valide. */
export function validateLogoFile(file: File): string | null {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return "Format non pris en charge. Utilisez une image PNG ou JPG.";
  }
  if (file.size > MAX_LOGO_SIZE) {
    return "Image trop lourde (1 Mo maximum).";
  }
  return null;
}

function extensionFor(type: string): string {
  return type === "image/png" ? "png" : "jpg";
}

/**
 * Téléverse un logo (Blob) dans le dossier du périmètre et renvoie le chemin
 * Storage à persister dans `logo_url`. L'ancien objet est supprimé (best effort).
 */
export async function uploadLogo(scopeId: string, blob: Blob, previousPath?: string): Promise<string> {
  if (!scopeId) throw new Error("Périmètre introuvable : impossible de téléverser le logo.");
  const path = `${scopeId}/logo-${Date.now()}.${extensionFor(blob.type || "image/jpeg")}`;
  const { error } = await supabase.storage.from(LOGOS_BUCKET).upload(path, blob, {
    upsert: true,
    contentType: blob.type || "image/jpeg",
  });
  if (error) throw error;
  if (previousPath && previousPath !== path) {
    await removeLogo(previousPath);
  }
  return path;
}

/** Téléverse un logo encodé en data URL (aperçu du formulaire). */
export async function uploadLogoDataUrl(scopeId: string, dataUrl: string, previousPath?: string): Promise<string> {
  const blob = await dataUrlToBlob(dataUrl);
  return uploadLogo(scopeId, blob, previousPath);
}

/** Supprime un logo (best effort : ne lève jamais). */
export async function removeLogo(path: string): Promise<void> {
  if (!path) return;
  const { error } = await supabase.storage.from(LOGOS_BUCKET).remove([path]);
  if (error) console.error("[Logos] suppression impossible", path, error.message);
}

/** Génère une URL signée (bucket privé) pour consulter un logo. */
export async function createSignedLogoUrl(path: string, expiresIn = 3600): Promise<string> {
  if (!path) return "";
  const { data, error } = await supabase.storage.from(LOGOS_BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    console.error("[Logos] URL signée impossible", path, error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(blob);
  });
}

/**
 * Résout un chemin Storage en data URL utilisable directement par jsPDF
 * (l'en-tête des documents). Renvoie une chaîne vide si indisponible.
 */
export async function resolveLogoDataUrl(path: string): Promise<string> {
  if (!path) return "";
  const signedUrl = await createSignedLogoUrl(path, 3600);
  if (!signedUrl) return "";
  try {
    const response = await fetch(signedUrl);
    if (!response.ok) return "";
    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch {
    return "";
  }
}